import { formatDisplayTitle } from '../formatProductTitle';
import { MAX_GIFT_GROUPS } from '../giftFinder/giftFinderGroups';
import { normalizeProductGiftTags } from '../productGiftTags';

/** Subcategory missing or generic — gift finder / shop groups need a real slug. */
export function subcategoryNeedsWork(subcategory) {
  const s = String(subcategory || '').trim().toLowerCase();
  return !s || s === 'all';
}

/**
 * @param {Record<string, Array<object>>} productsByCategory
 * @param {Array<{ slug: string, name?: string }>} categories
 */
export function catalogSubcategoryHealth(productsByCategory, categories) {
  const byCategory = [];
  let total = 0;
  let needsWork = 0;

  for (const cat of categories || []) {
    const slug = String(cat.slug || '').trim();
    if (!slug) continue;
    const list = productsByCategory[slug] || [];
    const catTotal = list.length;
    const catBad = list.filter((p) => subcategoryNeedsWork(p.subcategory)).length;
    total += catTotal;
    needsWork += catBad;
    if (catTotal > 0) {
      byCategory.push({
        slug,
        name: cat.name || slug,
        total: catTotal,
        needsWork: catBad,
        ok: catBad === 0,
      });
    }
  }

  return {
    total,
    needsWork,
    ok: needsWork === 0,
    byCategory,
  };
}

/**
 * Build gift-finder groups from taxonomy + product subcategories (merge by sub slug).
 * @param {Array<{ slug: string, name?: string, subcategories?: Array<{ slug: string, name?: string }> }>} categories
 * @param {Record<string, Array<{ subcategory?: string, type?: string }>>} productsByCategory
 */
export function buildGiftGroupsFromCatalog(categories, productsByCategory) {
  const groups = new Map();

  const addSub = (categorySlug, subSlug, labelHint) => {
    const sub = String(subSlug || '').trim().toLowerCase();
    const cat = String(categorySlug || '').trim().toLowerCase();
    if (!sub || sub === 'all' || !cat) return;

    const existing = groups.get(sub);
    if (existing) {
      if (!existing.categories.includes(cat)) {
        existing.categories.push(cat);
      }
      return;
    }
    groups.set(sub, {
      id: sub,
      label: formatDisplayTitle(String(labelHint || sub).replace(/-/g, ' ')),
      hint: '',
      icon: '·',
      categories: [cat],
      subcategories: [sub],
      types: [],
    });
  };

  for (const cat of categories || []) {
    const catSlug = String(cat.slug || '').trim().toLowerCase();
    if (!catSlug) continue;

    for (const sub of cat.subcategories || []) {
      if (sub?.slug) addSub(catSlug, sub.slug, sub.name || sub.slug);
    }

    for (const p of productsByCategory[catSlug] || []) {
      if (!subcategoryNeedsWork(p.subcategory)) {
        addSub(catSlug, p.subcategory, p.subcategory);
      }
      const typ = String(p.type || '').trim().toLowerCase();
      if (typ && typ !== 'all' && groups.size < MAX_GIFT_GROUPS) {
        const id = typ;
        if (!groups.has(id)) {
          groups.set(id, {
            id,
            label: formatDisplayTitle(typ.replace(/-/g, ' ')),
            hint: '',
            icon: '·',
            categories: [catSlug],
            subcategories: [],
            types: [typ],
          });
        } else {
          const g = groups.get(id);
          if (!g.categories.includes(catSlug)) g.categories.push(catSlug);
          if (!g.types.includes(typ)) g.types.push(typ);
        }
      }
    }
  }

  return [...groups.values()].slice(0, MAX_GIFT_GROUPS);
}

/**
 * Build gift-finder groups from product gift_tags (v2).
 * @param {Array<{ slug: string }>} categories
 * @param {Record<string, Array<{ gift_tags?: string[] }>>} productsByCategory
 */
export function buildGiftGroupsFromProductTags(categories, productsByCategory) {
  const groups = new Map();

  for (const cat of categories || []) {
    const catSlug = String(cat.slug || '').trim().toLowerCase();
    if (!catSlug) continue;
    for (const p of productsByCategory[catSlug] || []) {
      for (const tag of normalizeProductGiftTags(p.gift_tags)) {
        const existing = groups.get(tag);
        if (existing) {
          if (!existing.categories.includes(catSlug)) existing.categories.push(catSlug);
          continue;
        }
        groups.set(tag, {
          id: tag,
          label: formatDisplayTitle(tag.replace(/-/g, ' ')),
          hint: '',
          icon: '🏷️',
          categories: [catSlug],
          subcategories: [],
          types: [],
          tags: [tag],
        });
      }
    }
  }

  return [...groups.values()].slice(0, MAX_GIFT_GROUPS);
}

/** Products with zero gift tags (for admin health). */
export function catalogGiftTagHealth(productsByCategory, categories) {
  let total = 0;
  let untagged = 0;
  for (const cat of categories || []) {
    const slug = String(cat.slug || '').trim();
    if (!slug) continue;
    for (const p of productsByCategory[slug] || []) {
      total += 1;
      if (normalizeProductGiftTags(p.gift_tags).length === 0) untagged += 1;
    }
  }
  return { total, untagged, ok: untagged === 0 };
}

/** Starter recipients — edit slugs to match your gift group ids after import. */
export const SAMPLE_GIFT_RECIPIENTS = [
  {
    id: 'brother',
    label: 'Brother',
    hint: 'Classic rakhis',
    icon: '👦',
    category: 'rakhi',
    group: '',
    group_ids: 'single,band',
  },
  {
    id: 'sister',
    label: 'Sister',
    hint: 'Thoughtful picks',
    icon: '👧',
    category: 'rakhi',
    group: '',
    group_ids: 'single,bracelet',
  },
  {
    id: 'kids',
    label: 'Kids',
    hint: 'Fun designs',
    icon: '🧒',
    category: 'rakhi',
    group: '',
    group_ids: 'kids',
  },
];
